import React, { useId, useState } from 'react';
import { Dropdown, Tooltip } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { CustomDynamicSelect, CustomInput } from '@/components';
import { DotsIcon } from '@/assets/icons/icons';
import { isSimSensorId } from '@/common/utils/simulatorIds';
import type { SensorFormRow, SensorSelectRow } from '../sensorFormPayload';
import {
  productSummary,
  productTooltip,
  validateSensorDraft,
  type SensorDraftErrors,
} from '../sensorProductLinks';
import styles from './SensorSection.module.css';

type Editing = { index: number | null } | null;

export interface SensorSectionProps {
  rows: SensorFormRow[];
  onRowsChange: (rows: SensorFormRow[]) => void;
  sensorsList: SensorSelectRow[];
  getProductName: (id: string) => string;
  renderProductPicker: (
    value: string[],
    onChange: (ids: string[]) => void
  ) => React.ReactNode;
  onEditingChange: (editing: boolean) => void;
  isSimulatorSensor: (value: unknown) => boolean;
  suggestSensorId: (
    provider: string,
    rows: SensorFormRow[]
  ) => string | undefined;
  children?: React.ReactNode;
  readOnly?: boolean;
}

/** Compact sensor rows with an inline card to add or edit one (Figma 45595:363124). */
const SensorSection: React.FC<SensorSectionProps> = ({
  rows,
  onRowsChange,
  sensorsList,
  getProductName,
  renderProductPicker,
  onEditingChange,
  isSimulatorSensor,
  suggestSensorId,
  children,
  readOnly = false,
}) => {
  const [editing, setEditing] = useState<Editing>(null);
  const [draft, setDraft] = useState<SensorFormRow>({});
  const [errors, setErrors] = useState<SensorDraftErrors>({});
  const cardTitleId = useId();

  const providerName = (value?: string): string => {
    const match = sensorsList.find(
      (s) =>
        String(s.id) === String(value) || String(s.sensor_id) === String(value)
    );
    return match?.sensor_name ?? match?.sensor_label ?? '';
  };

  const open = (index: number | null): void => {
    setDraft(index === null ? {} : { ...rows[index] });
    setErrors({});
    setEditing({ index });
    onEditingChange(true);
  };

  const close = (): void => {
    setEditing(null);
    onEditingChange(false);
    setDraft({});
    setErrors({});
  };

  const save = (): void => {
    if (!editing) return;
    const otherRows = rows.filter((_, i) => i !== editing.index);
    const found = validateSensorDraft(
      draft,
      otherRows,
      isSimulatorSensor,
      isSimSensorId
    );
    if (found.sensor || found.sensor_tracking_id) {
      setErrors(found);
      return;
    }
    const row: SensorFormRow = {
      sensor: draft.sensor,
      sensor_tracking_id: (draft.sensor_tracking_id ?? '').trim(),
      product_ids: draft.product_ids ?? [],
    };
    onRowsChange(
      editing.index === null
        ? [...rows, row]
        : rows.map((existing, i) => (i === editing.index ? row : existing))
    );
    close();
  };

  const changeProvider = (value?: string): void => {
    const next = { ...draft, sensor: value };
    if (value && !draft.sensor_tracking_id) {
      const suggested = suggestSensorId(value, rows);
      if (suggested) next.sensor_tracking_id = suggested;
    }
    setDraft(next);
    setErrors((prev) => ({ ...prev, sensor: undefined }));
  };

  const card = (
    <div
      role='group'
      aria-labelledby={cardTitleId}
      className={styles.sensorCard}
    >
      <span id={cardTitleId} className={styles.sensorCardTitle}>
        {editing?.index === null ? 'New sensor' : 'Edit sensor'}
      </span>
      <div className={styles.sensorCardFields}>
        <label className={styles.sensorCardField}>
          <span className={styles.sensorCardLabel}>Sensor</span>
          <CustomDynamicSelect
            aria-label='Sensor'
            placeholder='Select'
            width='100%'
            height='36px'
            border='0px'
            bgColor='var(--light-gray)'
            textColor='var(--text-primary)'
            borderRadius='8px'
            allowClear
            options={sensorsList}
            label='sensor_label'
            showSearch={sensorsList.length > 10}
            value={draft.sensor}
            onChange={(value) => changeProvider(value as string | undefined)}
            status={errors.sensor ? 'error' : undefined}
          />
          {errors.sensor && (
            <span className={styles.sensorCardError}>{errors.sensor}</span>
          )}
        </label>
        <label className={styles.sensorCardField}>
          <span className={styles.sensorCardLabel}>Sensor ID</span>
          <CustomInput
            aria-label='Sensor ID'
            placeholder='Paste tracking number'
            height='36px'
            width='100%'
            border='0px'
            bgColor='var(--light-gray)'
            textColor='var(--text-primary)'
            borderRadius='8px'
            placeholderColor='var(--text-secondary)'
            lineHeight='38px'
            value={draft.sensor_tracking_id ?? ''}
            onChange={(e) => {
              setDraft({ ...draft, sensor_tracking_id: e.target.value });
              setErrors((prev) => ({ ...prev, sensor_tracking_id: undefined }));
            }}
            status={errors.sensor_tracking_id ? 'error' : undefined}
          />
          {errors.sensor_tracking_id && (
            <span className={styles.sensorCardError}>
              {errors.sensor_tracking_id}
            </span>
          )}
        </label>
        <div className={styles.sensorCardField}>
          <span className={styles.sensorCardLabel}>Product</span>
          {renderProductPicker(draft.product_ids ?? [], (ids) =>
            setDraft((prev) => ({ ...prev, product_ids: ids }))
          )}
        </div>
      </div>
      <div className={styles.sensorCardActions}>
        <button
          type='button'
          className={styles.sensorCardButton}
          onClick={close}
        >
          Cancel
        </button>
        <button
          type='button'
          className={`${styles.sensorCardButton} ${styles.isPrimary}`}
          onClick={save}
        >
          Save
        </button>
      </div>
    </div>
  );

  return (
    <div className={styles.sensorSection}>
      <span className={styles.sensorSectionTitle}>Sensors</span>
      <div className={styles.sensorSectionRows}>
        {rows.map((row, index) => {
          if (editing?.index === index) {
            return (
              <React.Fragment key={row.sensor_tracking_id}>
                {card}
              </React.Fragment>
            );
          }
          const names = (row.product_ids ?? []).map(getProductName);
          const summary = productSummary(names);
          return (
            <div className={styles.sensorRow} key={row.sensor_tracking_id}>
              <div className={styles.sensorRowIdentity}>
                <span className={styles.sensorRowProvider}>
                  {providerName(row.sensor)}
                </span>
                <span className={styles.sensorRowId}>
                  {row.sensor_tracking_id}
                </span>
              </div>
              <div className={styles.sensorRowEnd}>
                {names.length > 1 ? (
                  <Tooltip title={productTooltip(names)}>
                    <span className={styles.sensorRowSummary}>{summary}</span>
                  </Tooltip>
                ) : (
                  <span className={styles.sensorRowSummary}>{summary}</span>
                )}
                {!readOnly && (
                  <Dropdown
                    trigger={['click']}
                    disabled={editing !== null}
                    menu={{
                      items: [
                        { key: 'edit', label: 'Edit' },
                        { key: 'delete', label: 'Delete' },
                      ],
                      onClick: ({ key }) => {
                        if (key === 'edit') open(index);
                        else onRowsChange(rows.filter((_, i) => i !== index));
                      },
                    }}
                  >
                    <button
                      type='button'
                      className={styles.sensorRowMenu}
                      aria-label={`Sensor ${row.sensor_tracking_id} options`}
                      disabled={editing !== null}
                    >
                      <DotsIcon color='var(--text-primary)' />
                    </button>
                  </Dropdown>
                )}
              </div>
            </div>
          );
        })}
        {editing?.index === null && card}
      </div>
      {editing === null && !readOnly && (
        <button
          type='button'
          className={styles.addSensor}
          onClick={() => open(null)}
        >
          <span className={styles.addSensorIcon}>
            <PlusOutlined />
          </span>
          Add sensor
        </button>
      )}
      {children}
    </div>
  );
};

export default SensorSection;
